/**
 * LeaseDetailContent — Pure presentation component for lease details.
 *
 * Renders all lease fields in compact sections with warning banners
 * and action buttons. No hooks, no store access — all data via props.
 *
 * Used by LeaseDetailPanel shell (both LeasesPage and ramp view).
 */

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { PanelRight, Maximize2, Pencil, Building2, User, Plane, Calendar, DollarSign, RefreshCw, AlertTriangle, TrendingUp, Fuel, Shield, Repeat, FileText, StickyNote, ExternalLink, Ban, CheckCircle2, XCircle, LogOut } from 'lucide-react'
import type { LeaseTableRow, ContractAircraft } from '@/types/leases'
import {
  LEASE_STATUS_LABELS,
  LEASE_STATUS_VARIANTS,
  LEASE_TYPE_LABELS,
  RENEWAL_STATUS_LABELS,
  RENEWAL_STATUS_VARIANTS,
  CONTRACT_TYPE_LABELS,
  ESCALATION_TYPE_LABELS,
  RATE_TYPE_LABELS,
  FUEL_TYPE_LABELS,
  FUEL_PRICE_TYPE_LABELS,
  getDaysUntilExpiry,
  getContractTypeFromUnit,
  formatCurrency,
  getTermTypeLabel,
  canTerminate,
  canCancel,
  canComplete,
} from '@/types/leases'
import type { DetailPanelMode } from '@/stores/leaseViewStore'

// ============================================
// Types
// ============================================

export interface LeaseDetailContentProps {
  lease: LeaseTableRow
  aircraft?: ContractAircraft[]
  panelMode: DetailPanelMode
  onToggleMode?: () => void
  onEdit: () => void
  onRenew?: () => void
  onTerminate?: () => void
  onCancel?: () => void
  onComplete?: () => void
  onCheckOut?: () => void
  isCheckOutLoading?: boolean
  onNavigateToClient?: (clientName: string) => void
  onNavigateToMap?: (unitId: string) => void
}

// ============================================
// Warning Banner Logic
// ============================================

type WarningSeverity = 'critical' | 'warning' | 'notice'
type WarningAction = 'renew' | 'edit' | 'terminate' | 'complete' | 'checkout'

interface WarningBanner {
  severity: WarningSeverity
  message: string
  actions: WarningAction[]
}

function computeWarnings(lease: LeaseTableRow, hasCheckOut: boolean): WarningBanner[] {
  const warnings: WarningBanner[] = []
  const daysToExpiry = getDaysUntilExpiry(lease.end_date)

  // Non-active terminal statuses
  if (lease.status === 'expired') {
    warnings.push({ severity: 'critical', message: 'Lease expired', actions: ['renew'] })
  } else if (lease.status === 'terminated') {
    const actions: WarningAction[] = []
    if (hasCheckOut) actions.push('checkout')
    if (canComplete(lease.status)) actions.push('complete')
    warnings.push({
      severity: 'critical',
      message: lease.termination_reason
        ? `Lease terminated — ${lease.termination_reason}`
        : 'Lease terminated',
      actions,
    })
  } else if (lease.status === 'completed') {
    const actions: WarningAction[] = hasCheckOut ? ['checkout'] : []
    warnings.push({ severity: hasCheckOut ? 'warning' : 'notice', message: 'Lease completed', actions })
  } else if (lease.status === 'cancelled') {
    warnings.push({
      severity: 'notice',
      message: lease.cancellation_reason
        ? `Lease cancelled — ${lease.cancellation_reason}`
        : 'Lease cancelled',
      actions: [],
    })
  }

  // Active but end date has passed
  if (lease.status === 'active' && daysToExpiry !== null && daysToExpiry < 0) {
    warnings.push({
      severity: 'critical',
      message: `Lease expired ${Math.abs(daysToExpiry)}d ago`,
      actions: ['renew'],
    })
  }

  // Expiring within 30 days
  if (daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30) {
    const renewalNote =
      lease.renewal_status === 'not_started' ? ' \u2014 renewal not started' : ''
    warnings.push({
      severity: 'critical',
      message: `Expires in ${daysToExpiry}d${renewalNote}`,
      actions: ['renew'],
    })
  }
  // Expiring within 90 days
  else if (daysToExpiry !== null && daysToExpiry > 30 && daysToExpiry <= 90) {
    if (lease.renewal_status === 'not_started') {
      warnings.push({
        severity: 'warning',
        message: `Renewal not started \u2014 ${daysToExpiry}d until expiry`,
        actions: ['renew'],
      })
    }
  }

  return warnings
}

const SEVERITY_STYLES: Record<WarningSeverity, string> = {
  critical:
    'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200',
  warning:
    'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200',
  notice:
    'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950/30 dark:text-yellow-200',
}

// ============================================
// Main Component
// ============================================

export function LeaseDetailContent({
  lease,
  aircraft,
  panelMode,
  onToggleMode,
  onEdit,
  onRenew,
  onTerminate,
  onCancel,
  onComplete,
  onCheckOut,
    onNavigateToClient,
  onNavigateToMap,
}: LeaseDetailContentProps) {
  const daysToExpiry = getDaysUntilExpiry(lease.end_date)
  const contractType = getContractTypeFromUnit(lease.unit_type)
  const termLabel = getTermTypeLabel(lease)
  const warnings = computeWarnings(lease, !!onCheckOut)

  const warningActionHandler = (action: WarningAction) => {
    if (action === 'renew') onRenew?.()
    else if (action === 'edit') onEdit()
    else if (action === 'terminate') onTerminate?.()
    else if (action === 'complete') onComplete?.()
    else if (action === 'checkout') onCheckOut?.()
  }

  const WARNING_ACTION_CONFIG: Record<WarningAction, { label: string; icon: React.ComponentType<{ className?: string }>; destructive?: boolean }> = {
    renew: { label: 'Renew', icon: RefreshCw },
    edit: { label: 'Edit', icon: Pencil },
    terminate: { label: 'Terminate', icon: Ban, destructive: true },
    complete: { label: 'Complete', icon: CheckCircle2 },
    checkout: { label: 'Check Out', icon: LogOut, destructive: true },
  }

  return (
    <>
      {/* Header — single line */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-base font-bold shrink-0">
          {lease.contract_number}
        </span>
        <Badge variant={LEASE_STATUS_VARIANTS[lease.status]} className="text-xs shrink-0">
          {LEASE_STATUS_LABELS[lease.status]}
        </Badge>
        <Badge variant="outline" className="text-xs shrink-0">
          {LEASE_TYPE_LABELS[lease.type]}
        </Badge>
        <Badge variant="outline" className="text-xs shrink-0">
          {CONTRACT_TYPE_LABELS[contractType]}
        </Badge>
        <div className="flex-1" />
        {onToggleMode && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleMode}
            className="h-7 w-7 shrink-0"
          >
            {panelMode === 'sidePanel' ? (
              <Maximize2 className="h-4 w-4" />
            ) : (
              <PanelRight className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      {/* Warning Banners with inline actions */}
      {warnings.length > 0 && (
        <div className="space-y-2">
          {warnings.map((w, i) => (
            <div
              key={i}
              className={`rounded-lg border p-2.5 ${SEVERITY_STYLES[w.severity]}`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span className="text-sm font-medium flex-1">{w.message}</span>
                {w.actions.map((action) => {
                  const cfg = WARNING_ACTION_CONFIG[action]
                  const Icon = cfg.icon
                  return (
                    <Button
                      key={action}
                      variant="outline"
                      size="sm"
                      className={`h-6 px-2 text-xs shrink-0 bg-background/60 hover:bg-background ${cfg.destructive ? 'text-destructive hover:text-destructive border-destructive/30' : ''}`}
                      onClick={() => warningActionHandler(action)}
                    >
                      <Icon className="w-3 h-3 mr-1" />
                      {cfg.label}
                    </Button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Separator />

      {/* Client */}
      <Section icon={User} title="Client">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Company</span>
          {lease.client_name && onNavigateToClient ? (
            <button
              type="button"
              className="text-primary hover:underline cursor-pointer font-medium"
              onClick={() => onNavigateToClient(lease.client_name!)}
            >
              {lease.client_name}
            </button>
          ) : (
            <span>{lease.client_name ?? '\u2014'}</span>
          )}
        </div>
        <DetailRow label="Contact" value={lease.contact_name} />
      </Section>

      {/* Aircraft */}
      {aircraft && aircraft.length > 0 && (
        <Section icon={Plane} title="Aircraft">
          {aircraft.map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-sm">
              <span className="font-mono font-medium">{a.tail_number}</span>
              {a.aircraft_type_name && (
                <span className="text-muted-foreground">
                  {a.aircraft_type_name}
                </span>
              )}
              {a.is_primary && (
                <Badge variant="outline" className="text-xs">
                  Primary
                </Badge>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Space */}
      <Section icon={Building2} title="Space">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Unit</span>
          {lease.unit_code && onNavigateToMap ? (
            <button
              type="button"
              className="text-primary hover:underline cursor-pointer font-medium"
              onClick={() => onNavigateToMap(lease.unit_id)}
            >
              {lease.unit_code}
            </button>
          ) : (
            <span>{lease.unit_code ?? '\u2014'}</span>
          )}
        </div>
        <DetailRow label="Building" value={lease.building_name} />
        <DetailRow label="Location" value={lease.location_name} />
        <DetailRow label="Type" value={CONTRACT_TYPE_LABELS[contractType]} />
      </Section>

      {/* Terms */}
      <Section icon={Calendar} title="Terms">
        <DetailRow label="Type" value={LEASE_TYPE_LABELS[lease.type]} />
        <DetailRow label="Term" value={termLabel} />
        <DetailRow
          label="Start"
          value={
            lease.start_date
              ? new Date(lease.start_date).toLocaleDateString()
              : null
          }
        />
        <DetailRow
          label="End"
          value={
            lease.end_date
              ? new Date(lease.end_date).toLocaleDateString()
              : 'Open-ended'
          }
        />
        {daysToExpiry !== null && daysToExpiry >= 0 && (
          <DetailRow
            label="Days to Expiry"
            value={`${daysToExpiry}d`}
            valueClass={
              daysToExpiry <= 30
                ? 'text-destructive font-medium'
                : daysToExpiry <= 60
                  ? 'text-orange-600 dark:text-orange-500'
                  : daysToExpiry <= 90
                    ? 'text-yellow-600 dark:text-yellow-500'
                    : undefined
            }
          />
        )}
        {daysToExpiry !== null && daysToExpiry < 0 && (
          <DetailRow
            label="Expired"
            value={`${Math.abs(daysToExpiry)}d ago`}
            valueClass="text-destructive font-medium"
          />
        )}
        <DetailRow
          label="Auto-Renew"
          value={lease.auto_renew ? 'Yes' : 'No'}
        />
        {lease.renewal_notice_days != null && (
          <DetailRow
            label="Notice Period"
            value={`${lease.renewal_notice_days} days`}
          />
        )}
      </Section>

      {/* Financial */}
      <Section icon={DollarSign} title="Financial">
        <DetailRow
          label="Monthly Rate"
          value={formatCurrency(lease.monthly_rate)}
        />
        {lease.rate_basis && (
          <DetailRow
            label="Rate Basis"
            value={RATE_TYPE_LABELS[lease.rate_basis]}
          />
        )}
        {lease.security_deposit != null && (
          <DetailRow
            label="Security Deposit"
            value={formatCurrency(lease.security_deposit)}
          />
        )}
      </Section>

      {/* Escalation */}
      {lease.escalation_type && lease.escalation_type !== 'none' && (
        <Section icon={TrendingUp} title="Escalation">
          <DetailRow
            label="Type"
            value={ESCALATION_TYPE_LABELS[lease.escalation_type]}
          />
          {lease.escalation_pct != null && (
            <DetailRow label="Rate" value={`${lease.escalation_pct}%`} />
          )}
          {lease.escalation_date && (
            <DetailRow
              label="Next Date"
              value={new Date(lease.escalation_date).toLocaleDateString()}
            />
          )}
        </Section>
      )}

      {/* Fuel Commitment */}
      {(lease.fuel_commitment_gallons_monthly != null || lease.fuel_commitment_gallons_annual != null || lease.fuel_price_per_gallon != null || lease.fuel_type) && (
        <Section icon={Fuel} title="Fuel Commitment">
          {lease.fuel_type && (
            <DetailRow
              label="Fuel Type"
              value={FUEL_TYPE_LABELS[lease.fuel_type]}
            />
          )}
          {lease.fuel_commitment_gallons_monthly != null && (
            <DetailRow
              label="Monthly Commitment"
              value={`${lease.fuel_commitment_gallons_monthly.toLocaleString()} gal`}
            />
          )}
          {lease.fuel_commitment_gallons_annual != null && (
            <DetailRow
              label="Annual Commitment"
              value={`${lease.fuel_commitment_gallons_annual.toLocaleString()} gal`}
            />
          )}
          {lease.fuel_price_type && (
            <DetailRow
              label="Price Type"
              value={FUEL_PRICE_TYPE_LABELS[lease.fuel_price_type]}
            />
          )}
          {lease.fuel_price_per_gallon != null && (
            <DetailRow
              label="Price per Gallon"
              value={formatCurrency(lease.fuel_price_per_gallon)}
            />
          )}
          {lease.fuel_price_differential != null && (
            <DetailRow
              label="Price Differential"
              value={formatCurrency(lease.fuel_price_differential)}
            />
          )}
        </Section>
      )}

      {/* Guarantees */}
      {(lease.placement_guarantee || lease.requires_unblocked_access) && (
        <Section icon={Shield} title="Guarantees">
          {lease.placement_guarantee && (
            <DetailRow label="Placement Guarantee" value="Yes" />
          )}
          {lease.requires_unblocked_access && (
            <DetailRow label="Unblocked Access" value="Required" />
          )}
        </Section>
      )}

      {/* Transient Sublease */}
      {lease.allow_transient_sublease && (
        <Section icon={Repeat} title="Transient Sublease">
          <DetailRow label="Allowed" value="Yes" />
          {lease.transient_revenue_share_pct != null && (
            <DetailRow
              label="Revenue Share"
              value={`${lease.transient_revenue_share_pct}%`}
            />
          )}
        </Section>
      )}

      {/* Renewal */}
      <Section icon={RefreshCw} title="Renewal">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Status:</span>
          {lease.renewal_status ? (
            <Badge variant={RENEWAL_STATUS_VARIANTS[lease.renewal_status]}>
              {RENEWAL_STATUS_LABELS[lease.renewal_status]}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">&mdash;</span>
          )}
        </div>
        {lease.renewal_contacted_date && (
          <DetailRow
            label="Contacted"
            value={new Date(
              lease.renewal_contacted_date
            ).toLocaleDateString()}
          />
        )}
      </Section>

      {/* Documents */}
      {(lease.document_url || lease.signed_at) && (
        <Section icon={FileText} title="Documents">
          {lease.signed_at && (
            <DetailRow
              label="Signed"
              value={new Date(lease.signed_at).toLocaleDateString()}
            />
          )}
          {lease.document_url && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Document</span>
              <a
                href={lease.document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:underline"
              >
                View <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </Section>
      )}

      {/* Payment History — Coming Soon */}
      <Section icon={DollarSign} title="Payment History">
        <div className="rounded-md border border-dashed border-muted-foreground/30 p-3 text-center">
          <p className="text-sm text-muted-foreground">Payment history &mdash; Coming Soon</p>
        </div>
      </Section>

      {/* Termination Details */}
      {(lease.termination_reason || lease.termination_date) && (
        <Section icon={Ban} title="Termination">
          {lease.termination_date && (
            <DetailRow
              label="Date"
              value={new Date(lease.termination_date).toLocaleDateString()}
            />
          )}
          {lease.termination_reason && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {lease.termination_reason}
            </p>
          )}
        </Section>
      )}

      {/* Cancellation Details */}
      {lease.cancellation_reason && (
        <Section icon={XCircle} title="Cancellation">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {lease.cancellation_reason}
          </p>
        </Section>
      )}

      {/* Notes */}
      {lease.notes && (
        <Section icon={StickyNote} title="Notes">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {lease.notes}
          </p>
        </Section>
      )}

      <Separator />

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={onEdit}
        >
          <Pencil className="w-3.5 h-3.5 mr-1.5" />
          Edit
        </Button>

        {onRenew && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={onRenew}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Renew
          </Button>
        )}

        {onComplete && canComplete(lease.status) && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={onComplete}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
            Complete
          </Button>
        )}

        {onTerminate && canTerminate(lease.status) && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs text-destructive hover:text-destructive"
            onClick={onTerminate}
          >
            <Ban className="w-3.5 h-3.5 mr-1.5" />
            Terminate
          </Button>
        )}

        {onCancel && canCancel(lease.status) && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={onCancel}
          >
            <XCircle className="w-3.5 h-3.5 mr-1.5" />
            Cancel
          </Button>
        )}

        {/* <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs opacity-50"
              disabled
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" />
              Create Invoice
            </Button>
          </TooltipTrigger>
          <TooltipContent>Invoicing coming soon</TooltipContent>
        </Tooltip> */}
      </div>
    </>
  )
}

// ============================================
// Helpers
// ============================================

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
      </div>
      <div className="space-y-1 pl-6">{children}</div>
    </div>
  )
}

function DetailRow({
  label,
  value,
  valueClass,
}: {
  label: string
  value: string | null | undefined
  valueClass?: string
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={valueClass}>{value ?? '\u2014'}</span>
    </div>
  )
}
